import { BaseComponentImpl } from '../types/implementations';
import { ComponentLifetime, TemplatesByType, getLifetimeForDirection, getIntervalTypeForDirection, getIntervalValueForDirection } from '../Pages/Förvaltning/TemplatesEditor/types';
import { Building } from '../types/protocols';
import { BaseType, COMPONENT_TO_BASE_TYPE, MaintenanceIntervalType } from '../types/Enums';

export interface MaintenanceActivity {
    nextDate: string; // Specific date in YYYY-MM-DD format
    intervalType: MaintenanceIntervalType;
    intervalValue: number;
    cost?: number;
    suggestedCost?: number;
    costCalculation?: string;
    lastUpdated: string;
    disabled?: boolean;
}

export interface ComponentMaintenanceData {
    replacementDate?: number;
    replacementCost?: number;
    suggestedReplacementCost?: number;
    replacementCostCalculation?: string;
    maintenanceActivities: {
        [templateId: string]: MaintenanceActivity;
    };
}

export interface MaintenancePlan {
    components: {
        [componentKey: string]: ComponentMaintenanceData;
    };
    metadata: {
        savedAt: string;
        buildingKey: string;
    };
}

export interface YearlyActivity {
    year: number;
    activities: {
        componentKey: string;
        componentName: string;
        componentType: string;
        buildingKey?: string;
        buildingName?: string;
        activityType: 'replacement' | 'maintenance';
        activityName: string;
        templateId?: string;
        frequency: number;
        frequencyType: MaintenanceIntervalType;
        cost: number;
        costCalculation: string;
        baseType: string;
        nextDate?: string; // YYYY-MM-DD format
        // Inflation adjustment properties
        originalCost?: number;
        inflationYears?: number;
        inflationMultiplier?: number;
        priceSettingYear?: number;
    }[];
    totalCost: number;
    costsByBaseType: Record<string, number>;
}

export interface GenerateYearlyPlansOptions {
    building: Building;
    plan: MaintenancePlan;
    componentLifetimes: Record<string, ComponentLifetime>;
    maintenanceTemplates: TemplatesByType;
    componentConditions: Record<string, any>;
    yearsToShow: number;
    buildingName?: string;
}

/**
 * Convert interval to days
 */
export function intervalToDays(intervalType: MaintenanceIntervalType, intervalValue: number): number {
    switch (intervalType) {
        case MaintenanceIntervalType.days:
            return intervalValue;
        case MaintenanceIntervalType.weeks:
            return intervalValue * 7;
        case MaintenanceIntervalType.months:
            return intervalValue * 30.44; // Average days per month
        case MaintenanceIntervalType.years:
            return intervalValue * 365.25; // Account for leap years
        default:
            return intervalValue * 365.25;
    }
}

/**
 * Add interval to a date
 */
export function addIntervalToDate(date: Date, intervalType: MaintenanceIntervalType, intervalValue: number): Date {
    const newDate = new Date(date);
    
    switch (intervalType) {
        case MaintenanceIntervalType.days:
            newDate.setDate(newDate.getDate() + intervalValue);
            break;
        case MaintenanceIntervalType.weeks:
            newDate.setDate(newDate.getDate() + (intervalValue * 7));
            break;
        case MaintenanceIntervalType.months:
            newDate.setMonth(newDate.getMonth() + intervalValue);
            break;
        case MaintenanceIntervalType.years:
            newDate.setFullYear(newDate.getFullYear() + intervalValue);
            break;
    }
    
    return newDate;
}

/**
 * Format date to YYYY-MM-DD string
 */
export function formatDateToString(date: Date): string {
    return date.toISOString().split('T')[0];
}

/**
 * Parse date from YYYY-MM-DD string
 */
export function parseDateFromString(dateString: string): Date {
    return new Date(dateString + 'T00:00:00');
}

/**
 * Get next maintenance date based on current date and interval
 */
export function getNextMaintenanceDate(
    currentDate: string,
    intervalType: MaintenanceIntervalType,
    intervalValue: number
): string {
    const date = parseDateFromString(currentDate);
    const nextDate = addIntervalToDate(date, intervalType, intervalValue);
    return formatDateToString(nextDate);
}

/**
 * Translate interval type to Swedish
 */
export function translateIntervalType(intervalType: MaintenanceIntervalType): string {
    switch (intervalType) {
        case MaintenanceIntervalType.days:
            return 'dagar';
        case MaintenanceIntervalType.weeks:
            return 'veckor';
        case MaintenanceIntervalType.months:
            return 'månader';
        case MaintenanceIntervalType.years:
            return 'år';
        default:
            return 'år';
    }
}

/**
 * Sort activities by date, then by name as fallback
 */
export function sortActivitiesByDate(activities: any[]): any[] {
    return activities.sort((a, b) => {
        // Sort by nextDate if available, otherwise by activity name
        if (a.nextDate && b.nextDate) {
            return a.nextDate.localeCompare(b.nextDate);
        }
        if (a.nextDate && !b.nextDate) return -1;
        if (!a.nextDate && b.nextDate) return 1;
        return a.activityName.localeCompare(b.activityName);
    });
}

/**
 * Generate yearly maintenance plans for a single building
 */
export const generateYearlyPlansForBuilding = (options: GenerateYearlyPlansOptions): YearlyActivity[] => {
    const {
        building,
        plan,
        componentLifetimes,
        maintenanceTemplates,
        componentConditions,
        yearsToShow,
        buildingName
    } = options;

    if (!building?.model?.structure?.components || !plan || !plan.components || !componentLifetimes || !maintenanceTemplates) {
        return [];
    }

    const currentYear = new Date().getFullYear();
    const endYear = currentYear + yearsToShow;
    const yearlyPlans: YearlyActivity[] = [];

    // Initialize years
    for (let year = currentYear; year < endYear; year++) {
        yearlyPlans.push({
            year,
            activities: [],
            totalCost: 0,
            costsByBaseType: {}
        });
    }

    // Process each component
    Object.values(building.model.structure.components || {}).forEach(componentData => {
        if (!componentData.specificType) return;

        // Merge component conditions if available
        const componentKey = componentData.key;
        const conditionData = componentConditions[componentKey];
        if (conditionData) {
            componentData.condition = conditionData;
        }

        const component = new BaseComponentImpl(componentData);
        const componentName = component.displayName(building);
        const componentType = component.specificType;
        
        // Safety check for plan.components
        if (!plan.components || typeof plan.components !== 'object') {
            return;
        }
        
        const componentActive = plan.components[componentKey];

        if (!componentActive) return;

        // First, collect all replacement years to know when maintenance activities should reset
        const replacementYears: number[] = [];
        if (componentActive.replacementDate && componentType) {
            const lifetime = componentLifetimes[componentType];
            if (lifetime?.expectedLifetimeYears) {
                const directionalLifetime = getLifetimeForDirection(lifetime, component.cardinalDirection?.value || null);
                const expectedLifetime = directionalLifetime.expectedLifetimeYears;
                let nextReplacementYear = componentActive.replacementDate;

                while (nextReplacementYear < endYear) {
                    replacementYears.push(nextReplacementYear);
                    nextReplacementYear += expectedLifetime;
                }
            }
        }

        // Process replacement activities
        if (componentActive.replacementDate && componentType) {
            const lifetime = componentLifetimes[componentType];
            if (lifetime?.expectedLifetimeYears) {
                // Get directional lifetime for this component
                const directionalLifetime = getLifetimeForDirection(lifetime, component.cardinalDirection?.value || null);
                
                // Get replacement cost
                const replacementCost = componentActive.replacementCost || 0;
                const costCalculation = componentActive.replacementCostCalculation || 'Ingen kostnad angiven';
                
                // Get BaseType for this component
                const baseType = COMPONENT_TO_BASE_TYPE[componentType] || BaseType.Utility;
                const baseTypeKey = baseType.toString();

                // Add replacement activities for each replacement year
                replacementYears.forEach(replacementYear => {
                    const yearIndex = replacementYear - currentYear;
                    if (yearIndex >= 0 && yearIndex < yearlyPlans.length && replacementCost > 0) {
                        yearlyPlans[yearIndex].activities.push({
                            componentKey,
                            componentName,
                            componentType,
                            buildingKey: building.key,
                            buildingName,
                            activityType: 'replacement',
                            activityName: `Ersätt ${componentName}`,
                            frequency: directionalLifetime.expectedLifetimeYears,
                            frequencyType: MaintenanceIntervalType.years,
                            cost: replacementCost,
                            costCalculation,
                            baseType: baseTypeKey,
                            nextDate: `${replacementYear}-01-01`
                        });

                        yearlyPlans[yearIndex].totalCost += replacementCost;

                        // Initialize costsByBaseType if needed
                        if (!yearlyPlans[yearIndex].costsByBaseType[baseTypeKey]) {
                            yearlyPlans[yearIndex].costsByBaseType[baseTypeKey] = 0;
                        }
                        yearlyPlans[yearIndex].costsByBaseType[baseTypeKey] += replacementCost;
                    }
                });
            }
        }

        // Process maintenance activities with replacement reset logic
        if (componentActive.maintenanceActivities && componentType) {
            const templates = maintenanceTemplates[componentType] || [];
            
            Object.entries(componentActive.maintenanceActivities).forEach(([templateId, activity]) => {
                // Skip disabled activities
                if (activity.disabled === true) {
                    return;
                }
                
                if (activity.nextDate && activity.intervalValue) {
                    const template = templates.find(t => t.id === templateId);
                    if (!template) return;

                    const maintenanceCost = activity.cost || 0;
                    const costCalculation = activity.costCalculation || 'Ingen kostnad angiven';

                    // Get BaseType for this component
                    const baseType = COMPONENT_TO_BASE_TYPE[componentType] || BaseType.Utility;
                    const baseTypeKey = baseType.toString();

                    // Parse the next date and calculate maintenance schedule
                    const nextMaintenanceDate = parseDateFromString(activity.nextDate);
                    let currentInterval = activity.intervalValue;
                    let currentDate = new Date(nextMaintenanceDate);

                    while (currentDate.getFullYear() < endYear) {
                        const currentMaintenanceYear = currentDate.getFullYear();
                        const yearIndex = currentMaintenanceYear - currentYear;
                        
                        // Skip maintenance activities in replacement years
                        const isReplacementYear = replacementYears.includes(currentMaintenanceYear);
                        
                        if (yearIndex >= 0 && yearIndex < yearlyPlans.length && !isReplacementYear) {
                            yearlyPlans[yearIndex].activities.push({
                                componentKey,
                                componentName,
                                componentType,
                                buildingKey: building.key,
                                buildingName,
                                activityType: 'maintenance',
                                activityName: template.name,
                                templateId,
                                frequency: currentInterval,
                                frequencyType: activity.intervalType,
                                cost: maintenanceCost,
                                costCalculation,
                                baseType: baseTypeKey,
                                nextDate: formatDateToString(currentDate)
                            });

                            yearlyPlans[yearIndex].totalCost += maintenanceCost;

                            // Initialize costsByBaseType if needed
                            if (!yearlyPlans[yearIndex].costsByBaseType[baseTypeKey]) {
                                yearlyPlans[yearIndex].costsByBaseType[baseTypeKey] = 0;
                            }
                            yearlyPlans[yearIndex].costsByBaseType[baseTypeKey] += maintenanceCost;
                        }

                        // Check if the next maintenance date would be after a replacement
                        const nextMaintenanceDate = addIntervalToDate(currentDate, activity.intervalType, currentInterval);
                        const nextMaintenanceYear = nextMaintenanceDate.getFullYear();
                        const nextReplacement = replacementYears.find(year => year > currentMaintenanceYear && year <= nextMaintenanceYear);
                        
                        if (nextReplacement) {
                            // Reset maintenance schedule from replacement year with full interval
                            const replacementDate = new Date(nextReplacement, 0, 1);
                            currentDate = addIntervalToDate(replacementDate, activity.intervalType, currentInterval);
                        } else {
                            // Continue with normal interval
                            currentDate = nextMaintenanceDate;
                        }
                    }
                }
            });
        }
    });

    return yearlyPlans;
};

/**
 * Aggregate yearly plans from multiple buildings
 */
export const aggregateYearlyPlans = (buildingPlans: YearlyActivity[][]): YearlyActivity[] => {
    if (buildingPlans.length === 0) return [];

    // Get the year range from the first building's plans
    const firstBuilding = buildingPlans[0];
    if (firstBuilding.length === 0) return [];

    const aggregatedPlans: YearlyActivity[] = [];

    // Initialize aggregated plans with the same year structure
    firstBuilding.forEach(yearPlan => {
        aggregatedPlans.push({
            year: yearPlan.year,
            activities: [],
            totalCost: 0,
            costsByBaseType: {}
        });
    });

    // Aggregate data from all buildings
    buildingPlans.forEach(buildingYearlyPlans => {
        buildingYearlyPlans.forEach((yearPlan, yearIndex) => {
            if (yearIndex < aggregatedPlans.length) {
                // Add activities
                aggregatedPlans[yearIndex].activities.push(...yearPlan.activities);
                
                // Add to total cost
                aggregatedPlans[yearIndex].totalCost += yearPlan.totalCost;
                
                // Aggregate costs by base type
                Object.entries(yearPlan.costsByBaseType).forEach(([baseType, cost]) => {
                    if (!aggregatedPlans[yearIndex].costsByBaseType[baseType]) {
                        aggregatedPlans[yearIndex].costsByBaseType[baseType] = 0;
                    }
                    aggregatedPlans[yearIndex].costsByBaseType[baseType] += cost;
                });
            }
        });
    });

    return aggregatedPlans;
};
